<template>
  <b-card class="monthly-trend-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body>
    <template #header>
      <h6 class="mb-0">Monthly Lead & Closure Trend</h6>
    </template>
    <b-card-body>
      <apexchart type="line" height="350" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'MonthlyTrendChart',
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },

  data() {

    return {

      chartOptions: {

        chart: {

          type: 'line',

          height: 350,

          toolbar: {

            show: false,

          },

        },

        stroke: {

          curve: 'smooth',

        },

        xaxis: {

          categories: [], // Months will go here

        },

        yaxis: [

          {

            title: {

              text: 'Leads',

            },

          },

          {

            opposite: true,

            title: {

              text: 'Closures',

            },

          },

        ],

        tooltip: {

          y: {

            formatter: function (val) {

              return val.toFixed(0);

            },

          },

        },

      },

      series: [

        {

          name: 'Leads',

          data: [],

        },

        {

          name: 'Closures',

          data: [],

        },

      ],

    };

  },

  computed: {
    safeSeries() {
      const s = this.series || [];
      const mapped = s.map((item) => ({ name: item.name || '', data: Array.isArray(item.data) ? item.data : [] }));
      return mapped.length ? mapped : [{ name: 'Leads', data: [] }, { name: 'Closures', data: [] }];
    },
  },

  watch: {
    dashboardData: {
      deep: true,
      handler(val) {
        if (val && val.monthly_trend != null && hasRight(this.user, 'dashboard', 'view')) {
          this.processChartData(Array.isArray(val.monthly_trend) ? val.monthly_trend : []);
        }
      },
    },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.monthly_trend != null) this.processChartData(Array.isArray(this.dashboardData.monthly_trend) ? this.dashboardData.monthly_trend : []);
          return;
        }
        if (this.dashboardData && this.dashboardData.monthly_trend != null) { this.processChartData(Array.isArray(this.dashboardData.monthly_trend) ? this.dashboardData.monthly_trend : []); return; }
        this.fetchMonthlyTrendData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.monthly_trend != null) this.processChartData(Array.isArray(this.dashboardData.monthly_trend) ? this.dashboardData.monthly_trend : []);
            return;
          }
          this.fetchMonthlyTrendData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [{ name: 'Leads', data: [] }, { name: 'Closures', data: [] }];
        }
      },
    },
  },

  methods: {

    hasRight, // Make the helper available in the template

    async fetchMonthlyTrendData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.monthly_trend != null)) {
        if (this.dashboardData && this.dashboardData.monthly_trend != null) this.processChartData(Array.isArray(this.dashboardData.monthly_trend) ? this.dashboardData.monthly_trend : []);
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/monthly-trend', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(Array.isArray(raw) ? raw : []);

      } catch (error) {

        console.error('Error fetching monthly revenue trend data:', error);

        // Handle error

      }

    },

    processChartData(data) {
      if (!data || !Array.isArray(data) || data.length === 0) {

        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };

        this.series = [{ name: 'Leads', data: [] }, { name: 'Closures', data: [] }];

        return;

      }



      const categories = data.map(item => item.month);

      const leadsData = data.map(item => item.total_leads ?? item.leads ?? 0);

      const closuresData = data.map(item => item.total_closures ?? item.closures ?? 0);



      this.chartOptions = {

        ...this.chartOptions,

        xaxis: {

          categories: categories,

        },

      };



      this.series = [

        {

          name: 'Leads',

          data: leadsData,

        },

        {

          name: 'Closures',

          data: closuresData,

        },

      ];

    },

  },

};

</script>



<style scoped>

.monthly-trend-chart {

  margin-top: 20px;

}

</style>